import { View, Text } from '@react-pdf/renderer';
import { Span, SummaryData } from '..';
import { PricingModelTab } from '../../../common';
import { AccountingEventType, getDiscountValue, getTaxValue, getTotalValue } from '../../../pricing';
import { en } from '../../theme/languages/en';
import { invoiceSummaryValuesList } from '../../utils/invoiceSummary';
import { getCellValue } from '../../utils/invoiceTable';
import { getMinimumFeeRow } from './SummaryProductTable.utils';
import { styles } from './SummaryProductTable.module';

interface SummaryProductTableProps {
  product: PricingModelTab;
  isPartialPeriod?: boolean;
  tabIndex?: number;
  showOnlyTable?: boolean;
  style?: {};
  key?: any;
  constantDiscount?: number;
}

export const SummaryProductTable = ({
  product,
  style,
  showOnlyTable,
  isPartialPeriod,
  constantDiscount,
}: SummaryProductTableProps) => {
  const columns = product?.pricingModelTable?.columns?.filter((col) => !col.isHiddenInInvoice);
  let rows = product?.pricingModelTable?.rows;

  if (product?.accountingEventType != AccountingEventType.CREDIT && product?.minimumFee) {
    rows = getMinimumFeeRow(product);
  }

  return (
    <View style={[styles.container, style]} wrap>
      <View style={styles.headers} wrap={false}>
        {!showOnlyTable && (
          <Span style={styles.tableTitle} canBeMissingValue missingValue={product?.productName}>
            {product?.productName}
          </Span>
        )}

        <View style={styles.headerItems}>
          {columns?.map((column, index) => (
            <Text
              key={index}
              style={[
                styles.headerItem,
                { flex: index == 0 ? 2 : index == columns.length - 1 ? 1.5 : 1 },
                { textAlign: index == columns.length - 2 ? 'center' : index == columns.length - 1 ? 'right' : 'left' },
              ]}
            >
              {column?.title}
            </Text>
          ))}
        </View>
      </View>

      <View style={styles.flexColumn}>
        {rows
          .filter((row) => !row.isHiddenInInvoice)
          .map((row, index) => (
            <View key={index} style={styles.row} wrap={false}>
              {columns?.map((column, index) => (
                <Text
                  key={`${index}_${column.title}`}
                  wrap
                  style={[
                    styles.cell,
                    { flex: index == 0 ? 2 : index == columns.length - 1 ? 1.5 : 1 },
                    {
                      textAlign:
                        index == columns.length - 2 ? 'center' : index == columns.length - 1 ? 'right' : 'left',
                    },
                    {
                      marginRight: index == 0 ? 4 : 0,
                    },
                  ]}
                >
                  {getCellValue(row?.cells[column?.id])}
                </Text>
              ))}
            </View>
          ))}
      </View>

      {!showOnlyTable && (
        <SummaryData
          summaryItems={invoiceSummaryValuesList({
            currency: product?.currency,
            subTotalCalculatedValue: product?.subTotal,
            totalDue: product.total,
            discountCalculatedValue: product.totalDiscount,
            taxPercentage: product?.tax,
            taxCalculatedValue: product.totalTax,
            discount: product?.discount || constantDiscount,
            isPartialPeriod: isPartialPeriod,
          })}
        />
      )}

      {!showOnlyTable && (
        <View>
          <Span style={styles.note} canBeMissingValue missingValue={product?.note}>
            {en.note}
          </Span>

          <Span style={styles.text}>{product?.note}</Span>
        </View>
      )}
    </View>
  );
};
